// components/RelatorioVendas.js
// =============================================
// RELATÓRIO DE VENDAS — VERSÃO FINAL v21
// ✅ Filtro de cliente por NOME (dropdown pesquisável)
// ✅ Paginação em TODAS as tabelas (7 por página)
// ✅ Botão "Aplicar" para refazer a busca
// ✅ Botão "Baixar PDF" com diagnóstico detalhado
//    🔥 v21: CORRIGIDO PROBLEMA DO ";" NOS NOMES
//            • Todos os campos passam por uma função de escape
//            • Resolve o problema de colunas a serem empurradas
//            • Cabeçalho corporativo e valores formatados no padrão moeda
// =============================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import './RelatorioVendas.css';
import { Icon } from './SharedComponents';
import { FiFileText, FiBox, FiUsers, FiAlertTriangle } from 'react-icons/fi';

const API_BASE_URL = '/api';
const LIMITE_POR_PAGINA = 7;

const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT', codigo: 'MZN', casasDecimais: 2,
        separadorDecimal: ',', separadorMilhar: '.',
        posicaoSimbolo: 'depois', nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// =============================================
// COMPONENTE: Pesquisa de Cliente
// =============================================
function ClienteSearch({ clientes, clienteSelecionado, onSelecionar, onLimpar }) {
    const [termo, setTermo] = useState('');
    const [aberto, setAberto] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (clienteSelecionado) {
            const c = clientes.find(x => x._id === clienteSelecionado);
            if (c) setTermo(c.nome);
        } else {
            setTermo('');
        }
    }, [clienteSelecionado, clientes]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setAberto(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtrados = useMemo(() => {
        if (!termo || termo.trim() === '') return clientes.slice(0, 30);
        const t = termo.toLowerCase().trim();
        return clientes.filter(c =>
            (c.nome || '').toLowerCase().includes(t) ||
            (c.nuit || '').toLowerCase().includes(t) ||
            (c.email || '').toLowerCase().includes(t)
        ).slice(0, 30);
    }, [termo, clientes]);

    const handleSelect = (c) => {
        onSelecionar(c._id);
        setTermo(c.nome);
        setAberto(false);
    };

    const handleClear = () => {
        setTermo('');
        onLimpar();
        setAberto(false);
    };

    return (
        <div className="rv-cliente-search" ref={wrapperRef}>
            <div className="rv-cliente-input-wrap">
                <Icon name="search" size={14} className="rv-cliente-icon" />
                <input
                    type="text"
                    className="rv-cliente-input"
                    placeholder="Pesquisar por nome, NUIT ou email..."
                    value={termo}
                    onChange={(e) => {
                        setTermo(e.target.value);
                        setAberto(true);
                        if (e.target.value === '') onLimpar();
                    }}
                    onFocus={() => setAberto(true)}
                />
                {clienteSelecionado && (
                    <button type="button" className="rv-cliente-clear" onClick={handleClear} title="Limpar seleção">
                        <Icon name="close" size={14} />
                    </button>
                )}
            </div>

            {aberto && (
                <div className="rv-cliente-dropdown">
                    {filtrados.length === 0 ? (
                        <div className="rv-cliente-empty">
                            {clientes.length === 0 ? 'A carregar clientes...' : 'Nenhum cliente encontrado'}
                        </div>
                    ) : (
                        filtrados.map((c) => (
                            <div
                                key={c._id}
                                className={`rv-cliente-item ${clienteSelecionado === c._id ? 'ativo' : ''}`}
                                onClick={() => handleSelect(c)}
                            >
                                <div className="rv-cliente-item-info">
                                    <span className="rv-cliente-nome">{c.nome || 'Sem nome'}</span>
                                    {c.nuit && <span className="rv-cliente-nuit">NUIT: {c.nuit}</span>}
                                </div>
                                {clienteSelecionado === c._id && <Icon name="check" size={16} className="rv-cliente-check" />}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// =============================================
// COMPONENTE: Paginação
// =============================================
function Paginacao({ paginaAtual, totalPaginas, total, onMudarPagina, limite }) {
    if (totalPaginas <= 1) return null;

    const startItem = (paginaAtual - 1) * limite + 1;
    const endItem = Math.min(paginaAtual * limite, total);

    const getPageNumbers = (currentPage, totalPages) => {
        const pagesArray = [];
        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pagesArray.push(i);
            return pagesArray;
        }
        pagesArray.push(1);
        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 1);
        if (currentPage <= 3) { startPage = 2; endPage = Math.min(totalPages - 1, 4); }
        if (currentPage >= totalPages - 2) { startPage = Math.max(2, totalPages - 3); endPage = totalPages - 1; }
        if (startPage > 2) pagesArray.push('...');
        for (let i = startPage; i <= endPage; i++) pagesArray.push(i);
        if (endPage < totalPages - 1) pagesArray.push('...');
        pagesArray.push(totalPages);
        return pagesArray;
    };

    const pageNumbers = getPageNumbers(paginaAtual, totalPaginas);

    return (
        <div className="rv-paginacao">
            <div className="rv-paginacao-info">
                <span>Mostrando {startItem} - {endItem} de {total} registos</span>
            </div>
            <div className="rv-paginacao-controls">
                <button className="rv-paginacao-btn" onClick={() => onMudarPagina(paginaAtual - 1)} disabled={paginaAtual === 1} title="Página anterior">
                    <Icon name="chevron_left" size={18} />
                </button>
                <div className="rv-paginacao-numeros">
                    {pageNumbers.map((p, index) => {
                        if (p === '...') return <span key={`dots-${index}`} className="rv-paginacao-dots">…</span>;
                        return (
                            <button key={p} className={`rv-paginacao-numero ${paginaAtual === p ? 'active' : ''}`} onClick={() => onMudarPagina(p)}>
                                {p}
                            </button>
                        );
                    })}
                </div>
                <button className="rv-paginacao-btn" onClick={() => onMudarPagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} title="Página seguinte">
                    <Icon name="chevron_right" size={18} />
                </button>
            </div>
        </div>
    );
}

// =============================================
// Nota de proveniência
// =============================================
function NotaFonte({ imposto }) {
    if (!imposto) return null;
    const numFechos = imposto.numeroFechos ?? 0;
    const fonte = imposto.fonte || 'contabilidade';
    return (
        <div className="rv-nota-fonte">
            <span className="rv-nota-fonte-label">📌 Fonte:</span>
            <span className="rv-nota-fonte-valor">{fonte === 'contabilidade' ? 'Contabilidade' : fonte}</span>
            {numFechos > 0 ? (
                <span className="rv-nota-fonte-detalhe">({numFechos} fecho{numFechos > 1 ? 's' : ''} gravado{numFechos > 1 ? 's' : ''})</span>
            ) : (
                <span className="rv-nota-fonte-aviso">⚠️ Sem fechos no período selecionado</span>
            )}
        </div>
    );
}

// =============================================
// Bloco detalhado de ISPC
// =============================================
function BlocoISPC({ imposto, formatarMoeda, taxa }) {
    if (!imposto || imposto.tipo !== 'ISPC' || !imposto.detalhe || imposto.detalhe.length === 0) return null;

    const detalhe = imposto.detalhe;
    const totalVolume = detalhe.reduce((s, t) => s + (t.volumeNegocios || 0), 0);
    const totalISPC = detalhe.reduce((s, t) => s + (t.ispc || 0), 0);
    const totalFaturas = detalhe.reduce((s, t) => s + (t.numFaturas || 0), 0);

    return (
        <div className="rv-bloco-ispc">
            <h3 className="rv-bloco-ispc-titulo">📊 Apuramento ISPC — {taxa}% sobre Volume de Negócios (Trimestral)</h3>
            <div className="rv-tabela-wrapper">
                <table className="rv-tabela rv-tabela-ispc">
                    <thead>
                        <tr>
                            <th>Trimestre</th>
                            <th className="text-right">Nº Faturas</th>
                            <th className="text-right">Volume de Negócios</th>
                            <th className="text-right">ISPC {taxa}%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {detalhe.map((t, i) => (
                            <tr key={i}>
                                <td><strong>{t.label}</strong></td>
                                <td className="text-right">{t.numFaturas}</td>
                                <td className="text-right">{formatarMoeda(t.volumeNegocios)}</td>
                                <td className="text-right laranja destaque">{formatarMoeda(t.ispc)}</td>
                            </tr>
                        ))}
                        <tr className="rv-linha-total">
                            <td><strong>TOTAL</strong></td>
                            <td className="text-right"><strong>{totalFaturas}</strong></td>
                            <td className="text-right"><strong>{formatarMoeda(totalVolume)}</strong></td>
                            <td className="text-right laranja destaque"><strong>{formatarMoeda(totalISPC)}</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <NotaFonte imposto={imposto} />
        </div>
    );
}

// =============================================
// Bloco de apuramento IVA
// =============================================
function BlocoIVA({ imposto, formatarMoeda, taxa }) {
    if (!imposto || imposto.tipo !== 'IVA') return null;

    const temDeducoes = imposto.deducoes && imposto.deducoes.ivaDedutivel > 0;
    const d = imposto.deducoes || {
        ivaDedutivel: 0, ivaDedutivelDespesas: 0, ivaDedutivelStock: 0, ivaDedutivelCompras: 0,
        baseDespesas: 0, baseStock: 0, baseCompras: 0, numeroCompras: 0
    };

    const ivaBruto = imposto.bruto || 0;
    const ivaAEntregar = imposto.aEntregar || 0;
    const ivaARecuperar = imposto.aRecuperar || 0;
    const base = imposto.base || 0;

    return (
        <div className="rv-bloco-iva">
            <h3 className="rv-bloco-iva-titulo">🧾 Apuramento IVA {taxa}% — {imposto.label}</h3>
            <div className="rv-tabela-wrapper">
                <table className="rv-tabela rv-tabela-iva">
                    <thead>
                        <tr>
                            <th>Descrição</th>
                            <th className="text-right">Base Tributável</th>
                            <th className="text-right">IVA</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>IVA Cobrado (vendas)</strong></td>
                            <td className="text-right">{formatarMoeda(base)}</td>
                            <td className="text-right verde destaque">+ {formatarMoeda(ivaBruto)}</td>
                        </tr>
                        {temDeducoes && (
                            <>
                                <tr>
                                    <td>IVA Dedutível — Despesas</td>
                                    <td className="text-right">{formatarMoeda(d.baseDespesas || 0)}</td>
                                    <td className="text-right vermelho">− {formatarMoeda(d.ivaDedutivelDespesas || 0)}</td>
                                </tr>
                                <tr>
                                    <td>IVA Dedutível — Stock</td>
                                    <td className="text-right">{formatarMoeda(d.baseStock || 0)}</td>
                                    <td className="text-right vermelho">− {formatarMoeda(d.ivaDedutivelStock || 0)}</td>
                                </tr>
                                <tr>
                                    <td>IVA Dedutível — Compras a Fornecedor ({d.numeroCompras || 0})</td>
                                    <td className="text-right">{formatarMoeda(d.baseCompras || 0)}</td>
                                    <td className="text-right vermelho">− {formatarMoeda(d.ivaDedutivelCompras || 0)}</td>
                                </tr>
                            </>
                        )}
                        <tr className="rv-linha-total">
                            <td>
                                <strong>
                                    {temDeducoes
                                        ? (ivaAEntregar > 0 ? 'IVA A ENTREGAR' : 'IVA A RECUPERAR')
                                        : 'IVA COBRADO (SEM DEDUÇÕES REGISTADAS)'}
                                </strong>
                            </td>
                            <td className="text-right"><strong>—</strong></td>
                            <td className="text-right laranja destaque">
                                <strong>
                                    {temDeducoes
                                        ? formatarMoeda(ivaAEntregar > 0 ? ivaAEntregar : ivaARecuperar)
                                        : formatarMoeda(ivaBruto)}
                                </strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <NotaFonte imposto={imposto} />
        </div>
    );
}

export default function RelatorioVendas() {
    // ==================== ESTADOS ====================
    const [abaAtiva, setAbaAtiva] = useState('faturas');
    const [carregando, setCarregando] = useState(false);
    const [exportandoPDF, setExportandoPDF] = useState(null);
    const [erro, setErro] = useState(null);
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);
    const [regime, setRegime] = useState({ tipo: 'Normal', taxaIVA: 16, taxaISPC: 0 });

    // ==================== FILTROS ====================
    const [filtrosRascunho, setFiltrosRascunho] = useState({
        dataInicio: '', dataFim: '', clienteId: '', vendedorId: '',
        status: 'Emitida,Aberta,Pago,Vencida'
    });
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        dataInicio: '', dataFim: '', clienteId: '', vendedorId: '',
        status: 'Emitida,Aberta,Pago,Vencida'
    });

    // ==================== PAGINAÇÃO ====================
    const [paginas, setPaginas] = useState({ faturas: 1, produtos: 1, vendedores: 1, inadimplencia: 1 });

    // ==================== DADOS ====================
    const [faturas, setFaturas] = useState({ dados: [], totais: {}, paginacao: {} });
    const [produtos, setProdutos] = useState({ dados: [], paginacao: {} });
    const [vendedores, setVendedores] = useState({ dados: [], paginacao: {} });
    const [inadimplencia, setInadimplencia] = useState({ dados: [], totais: {}, paginacao: {} });
    const [vendedoresDisponiveis, setVendedoresDisponiveis] = useState([]);
    const [clientes, setClientes] = useState([]);

    // ==================== API ====================
    const api = useMemo(() => axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache'
        }
    }), []);

    // ==================== FORMATAÇÃO ====================
    const formatarMoeda = useCallback((valor) => {
        const cfg = empresaConfig?.moeda || CONFIG_PADRAO.moeda;
        const sepDec = cfg.separadorDecimal || ',';
        const sepMil = cfg.separadorMilhar || '.';
        const casasDec = cfg.casasDecimais ?? 2;
        const simbolo = cfg.simbolo || 'MT';

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${sepDec}${'0'.repeat(casasDec)} ${simbolo}`;
        }

        let v = Number(valor).toFixed(casasDec).replace('.', sepDec);
        const partes = v.split(sepDec);
        partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
        v = partes.join(sepDec);

        return cfg.posicaoSimbolo === 'antes' ? `${simbolo} ${v}` : `${v} ${simbolo}`;
    }, [empresaConfig]);

    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();
        return `${dia}/${mes}/${ano}`;
    }, []);

    // =============================================
    // APLICAR CONFIGURAÇÃO
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao || {};
        const emp = resData.empresa || {};
        const empCfg = emp.configuracoes || {};

        const moedaFinal = cfg.moeda || emp.moeda || empCfg.moeda || resData.moeda || null;
        const formatacaoFinal = cfg.formatacao || emp.formatacao || empCfg.formatacao || resData.formatacao || null;
        const nomeEmpresa = emp.nome || resData.empresaNome || null;
        const nuitEmpresa = emp.nuit || resData.empresaNuit || null;
        const emailEmpresa = emp.email || null;
        const telefoneEmpresa = emp.telefone || null;
        const imagemPerfil = emp.imagemPerfil || null;
        const moradaEmpresa = emp.morada || null;

        if (moedaFinal || formatacaoFinal || nomeEmpresa) {
            setEmpresaConfig(prev => {
                const novo = {
                    ...prev,
                    moeda: { ...(prev.moeda || {}), ...(moedaFinal || {}) },
                    formatacao: {
                        ...(prev.formatacao || {}),
                        data: { ...((prev.formatacao || {}).data || {}), ...((formatacaoFinal || {}).data || {}) },
                        numero: { ...((prev.formatacao || {}).numero || {}), ...((formatacaoFinal || {}).numero || {}) }
                    }
                };
                if (nomeEmpresa) novo.nome = nomeEmpresa;
                if (nuitEmpresa) novo.nuit = nuitEmpresa;
                if (emailEmpresa) novo.email = emailEmpresa;
                if (telefoneEmpresa) novo.telefone = telefoneEmpresa;
                if (imagemPerfil) novo.imagemPerfil = imagemPerfil;
                if (moradaEmpresa) novo.morada = moradaEmpresa;
                return novo;
            });
        }
        if (resData.regime) setRegime(resData.regime);
    }, []);

    // ==================== CARREGAR VENDEDORES DISPONÍVEIS ====================
    const carregarVendedoresDisponiveis = useCallback(async () => {
        try {
            const { data } = await api.get('/relatorios/vendas/vendedores-disponiveis');
            if (data.success) setVendedoresDisponiveis(data.vendedores || []);
        } catch (err) { console.error('❌ Erro vendedores disponíveis:', err); }
    }, [api]);

    // ==================== CARREGAR CLIENTES ====================
    const carregarClientes = useCallback(async () => {
        try {
            const { data } = await api.get('/clientes?limit=1000');
            const lista = data?.data || data?.clientes || (Array.isArray(data) ? data : []);
            setClientes(lista);
        } catch (err) { console.error('❌ Erro clientes:', err); setClientes([]); }
    }, [api]);

    // ==================== CARREGAR FATURAS ====================
    const carregarFaturas = useCallback(async (pagina = 1) => {
        setCarregando(true); setErro(null);
        try {
            const params = new URLSearchParams();
            Object.entries(filtrosAplicados).forEach(([k, v]) => {
                if (v !== '' && v !== null && v !== undefined) params.append(k, v);
            });
            params.append('pagina', pagina);
            params.append('limite', LIMITE_POR_PAGINA);

            const { data } = await api.get(`/relatorios/vendas/faturas?${params}`);

            if (data.success) {
                setFaturas({
                    dados: data.faturas || [],
                    totais: data.totais || {},
                    paginacao: data.paginacao || { pagina, totalPaginas: 1, total: 0 }
                });
                aplicarConfiguracao(data);
            } else { setErro(data.message || 'Erro ao carregar faturas'); }
        } catch (err) {
            console.error('❌ Erro faturas:', err);
            setErro(err.response?.data?.message || err.message || 'Erro ao carregar faturas');
            setFaturas({ dados: [], totais: {}, paginacao: {} });
        } finally { setCarregando(false); }
    }, [filtrosAplicados, api, aplicarConfiguracao]);

    // ==================== CARREGAR PRODUTOS ====================
    const carregarProdutos = useCallback(async (pagina = 1) => {
        setCarregando(true); setErro(null);
        try {
            const params = new URLSearchParams();
            if (filtrosAplicados.dataInicio) params.append('dataInicio', filtrosAplicados.dataInicio);
            if (filtrosAplicados.dataFim) params.append('dataFim', filtrosAplicados.dataFim);
            params.append('pagina', pagina);
            params.append('limite', LIMITE_POR_PAGINA);

            const { data } = await api.get(`/relatorios/vendas/produtos?${params}`);

            if (data.success) {
                setProdutos({ dados: data.produtos || [], paginacao: data.paginacao || { pagina, totalPaginas: 1, total: 0 } });
                aplicarConfiguracao(data);
            } else { setErro(data.message || 'Erro ao carregar produtos'); }
        } catch (err) {
            console.error('❌ Erro produtos:', err);
            setErro(err.response?.data?.message || err.message || 'Erro ao carregar produtos');
            setProdutos({ dados: [], paginacao: {} });
        } finally { setCarregando(false); }
    }, [filtrosAplicados.dataInicio, filtrosAplicados.dataFim, api, aplicarConfiguracao]);

    // ==================== CARREGAR VENDEDORES ====================
    const carregarVendedores = useCallback(async (pagina = 1) => {
        setCarregando(true); setErro(null);
        try {
            const params = new URLSearchParams();
            if (filtrosAplicados.dataInicio) params.append('dataInicio', filtrosAplicados.dataInicio);
            if (filtrosAplicados.dataFim) params.append('dataFim', filtrosAplicados.dataFim);
            params.append('pagina', pagina);
            params.append('limite', LIMITE_POR_PAGINA);

            const { data } = await api.get(`/relatorios/vendas/vendedores?${params}`);

            if (data.success) {
                setVendedores({ dados: data.vendedores || [], paginacao: data.paginacao || { pagina, totalPaginas: 1, total: 0 } });
                aplicarConfiguracao(data);
            } else { setErro(data.message || 'Erro ao carregar vendedores'); }
        } catch (err) {
            console.error('❌ Erro vendedores:', err);
            setErro(err.response?.data?.message || err.message || 'Erro ao carregar vendedores');
            setVendedores({ dados: [], paginacao: {} });
        } finally { setCarregando(false); }
    }, [filtrosAplicados.dataInicio, filtrosAplicados.dataFim, api, aplicarConfiguracao]);

    // ==================== CARREGAR INADIMPLÊNCIA ====================
    const carregarInadimplencia = useCallback(async (pagina = 1) => {
        setCarregando(true); setErro(null);
        try {
            const params = new URLSearchParams();
            if (filtrosAplicados.dataInicio) params.append('dataInicio', filtrosAplicados.dataInicio);
            if (filtrosAplicados.dataFim) params.append('dataFim', filtrosAplicados.dataFim);
            params.append('pagina', pagina);
            params.append('limite', LIMITE_POR_PAGINA);

            const { data } = await api.get(`/relatorios/vendas/inadimplencia?${params}`);

            if (data.success) {
                setInadimplencia({ dados: data.faturas || [], totais: data.totais || {}, paginacao: data.paginacao || { pagina, totalPaginas: 1, total: 0 } });
                aplicarConfiguracao(data);
            } else { setErro(data.message || 'Erro ao carregar inadimplência'); }
        } catch (err) {
            console.error('❌ Erro inadimplência:', err);
            setErro(err.response?.data?.message || err.message || 'Erro ao carregar inadimplência');
            setInadimplencia({ dados: [], totais: {}, paginacao: {} });
        } finally { setCarregando(false); }
    }, [filtrosAplicados.dataInicio, filtrosAplicados.dataFim, api, aplicarConfiguracao]);

    // ==================== APLICAR / LIMPAR ====================
    const aplicarFiltros = () => {
        setFiltrosAplicados({ ...filtrosRascunho });
        setPaginas({ faturas: 1, produtos: 1, vendedores: 1, inadimplencia: 1 });
    };
    const limparFiltros = () => {
        const vazio = { dataInicio: '', dataFim: '', clienteId: '', vendedorId: '', status: 'Emitida,Aberta,Pago,Vencida' };
        setFiltrosRascunho(vazio);
        setFiltrosAplicados(vazio);
        setPaginas({ faturas: 1, produtos: 1, vendedores: 1, inadimplencia: 1 });
    };
    const mudarPagina = (aba, novaPagina) => { setPaginas(prev => ({ ...prev, [aba]: novaPagina })); };

    // ==================== EFEITOS ====================
    useEffect(() => {
        carregarVendedoresDisponiveis();
        carregarClientes();
    }, [carregarVendedoresDisponiveis, carregarClientes]);

    useEffect(() => {
        if (abaAtiva === 'faturas') carregarFaturas(paginas.faturas);
        if (abaAtiva === 'produtos') carregarProdutos(paginas.produtos);
        if (abaAtiva === 'vendedores') carregarVendedores(paginas.vendedores);
        if (abaAtiva === 'inadimplencia') carregarInadimplencia(paginas.inadimplencia);
    }, [abaAtiva, filtrosAplicados, paginas, carregarFaturas, carregarProdutos, carregarVendedores, carregarInadimplencia]);

    // ==================== HANDLERS ====================
    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltrosRascunho(prev => ({ ...prev, [name]: value }));
    };
    const handleClienteSelecionar = (id) => { setFiltrosRascunho(prev => ({ ...prev, clienteId: id })); };
    const handleClienteLimpar = () => { setFiltrosRascunho(prev => ({ ...prev, clienteId: '' })); };

    // ==================== INFO DO IMPOSTO ====================
    const obterInfoImposto = useCallback(() => {
        const imposto = faturas.totais?.imposto;
        if (imposto) {
            return {
                tipo: imposto.tipo || regime.tipo,
                label: imposto.label || (regime.tipo === 'ISPC' ? 'ISPC' : regime.tipo === 'Isento' ? 'Isento' : 'IVA'),
                valor: imposto.bruto ?? imposto.valor ?? 0,
                bruto: imposto.bruto ?? 0,
                liquido: imposto.liquido ?? imposto.bruto ?? 0,
                aEntregar: imposto.aEntregar ?? 0,
                aRecuperar: imposto.aRecuperar ?? 0,
                taxa: imposto.taxa || regime.taxaIVA || 0,
                base: imposto.base ?? 0,
                volumeNegocios: imposto.volumeNegocios ?? null,
                deducoes: imposto.deducoes ?? null,
                detalhe: imposto.detalhe ?? null,
                fonte: imposto.fonte || 'contabilidade',
                numeroFechos: imposto.numeroFechos ?? 0
            };
        }
        if (regime.tipo === 'ISPC') {
            const base = faturas.totais?.totalFaturado || 0;
            const ispc = base * (regime.taxaISPC / 100);
            return { tipo: 'ISPC', label: `ISPC ${regime.taxaISPC}%`, valor: ispc, bruto: ispc, liquido: ispc, aEntregar: ispc, aRecuperar: 0, taxa: regime.taxaISPC, base: 0, volumeNegocios: base, deducoes: null, detalhe: null, fonte: 'fallback', numeroFechos: 0 };
        }
        if (regime.tipo === 'Isento') {
            return { tipo: 'Isento', label: 'Isento', valor: 0, bruto: 0, liquido: 0, aEntregar: 0, aRecuperar: 0, taxa: 0, base: 0, volumeNegocios: null, deducoes: null, detalhe: null, fonte: 'fallback', numeroFechos: 0 };
        }
        const ivaBruto = faturas.totais?.totalIva || 0;
        return { tipo: 'IVA', label: `IVA ${regime.taxaIVA}%`, valor: ivaBruto, bruto: ivaBruto, liquido: ivaBruto, aEntregar: ivaBruto, aRecuperar: 0, taxa: regime.taxaIVA, base: faturas.totais?.totalFaturado || 0, volumeNegocios: null, deducoes: null, detalhe: null, fonte: 'fallback', numeroFechos: 0 };
    }, [faturas.totais, regime]);

    // ==================== COLUNA DE IMPOSTO ====================
    const mostrarColunaImposto = regime.tipo === 'Normal';

    // =============================================
    // 🔥 EXPORTAR CSV (Embelezado e Formatado)
    // =============================================
    const exportarCSV = async () => {
        try {
            const endpointPorAba = {
                faturas: '/relatorios/vendas/faturas',
                produtos: '/relatorios/vendas/produtos',
                vendedores: '/relatorios/vendas/vendedores',
                inadimplencia: '/relatorios/vendas/inadimplencia'
            };

            const endpoint = endpointPorAba[abaAtiva];
            if (!endpoint) { alert('Aba inválida.'); return; }

            const params = new URLSearchParams();
            Object.entries(filtrosAplicados).forEach(([k, v]) => {
                if (v !== '' && v !== null && v !== undefined) params.append(k, v);
            });

            params.append('pagina', '1');
            params.append('limite', '999999');

            const { data } = await api.get(`${endpoint}?${params.toString()}`);
            if (!data?.success) { alert('Erro ao preparar exportação.'); return; }

            const linhas = [];

            // 🔥 FUNÇÃO DE ESCAPE: Resolve o problema do ";" nos nomes
            const escape = (v) => {
                if (v === undefined || v === null) return '""';
                return `"${String(v).replace(/"/g, '""')}"`;
            };

            // 1. CABEÇALHO CORPORATIVO
            linhas.push([escape('RELATÓRIO DE VENDAS'), '', '', '', '', '', '', '', '', '']);
            linhas.push([escape('EMPRESA:'), escape(empresaConfig.nome || 'N/A'), '', '', '', '', '', '', '', '']);
            linhas.push([escape('NUIT:'), `="${empresaConfig.nuit || 'N/A'}"`, '', '', '', '', '', '', '', '']);
            linhas.push([escape('DATA EXPORTAÇÃO:'), escape(new Date().toLocaleString('pt-PT')), '', '', '', '', '', '', '', '']);
            linhas.push([escape('PERÍODO:'), escape(`${filtrosAplicados.dataInicio || 'Início'} até ${filtrosAplicados.dataFim || 'Fim'}`), '', '', '', '', '', '', '', '']);
            linhas.push(['', '', '', '', '', '', '', '', '', '']); // Linha vazia

            if (abaAtiva === 'faturas') {
                const cabecalho = ['Nº Fatura', 'Data', 'Cliente', 'NUIT', 'Vendedor', 'Status', 'Subtotal'];
                if (mostrarColunaImposto) cabecalho.push(`IVA ${regime.taxaIVA}%`);
                cabecalho.push('Total', 'Pago', 'Saldo');
                linhas.push(cabecalho.map(escape));

                (data.faturas || []).forEach(f => {
                    const row = [
                        f.numero,
                        formatarData(f.dataEmissao),
                        f.cliente?.nome || '-',
                        `="${f.cliente?.nuit || '-'}"`,
                        f.vendedor?.nome || '-',
                        f.status,
                        formatarMoeda(f.subtotal)
                    ];
                    if (mostrarColunaImposto) row.push(formatarMoeda(f.iva));
                    row.push(formatarMoeda(f.total));
                    row.push(formatarMoeda(f.valorPago));
                    row.push(formatarMoeda(f.saldo));
                    linhas.push(row.map(escape));
                });

                // Rodapé Fiscal
                if (data.totais?.imposto) {
                    const im = data.totais.imposto;
                    linhas.push(['', '', '', '', '', '', '', '', '', '']);
                    linhas.push([escape('--- APURAMENTO FISCAL ---'), '', '', '', '', '', '', '', '', '']);
                    linhas.push([escape('Fonte'), escape(im.fonte === 'contabilidade' ? 'Contabilidade' : im.fonte), '', '', '', '', '', '', '', '']);
                    linhas.push([escape('Fechos gravados'), escape(im.numeroFechos || 0), '', '', '', '', '', '', '', '']);

                    if (im.tipo === 'ISPC') {
                        linhas.push([escape('Tipo'), escape(`ISPC ${im.taxa}%`), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('Volume de Negócios'), escape(formatarMoeda(im.volumeNegocios)), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('ISPC a Entregar'), escape(formatarMoeda(im.liquido)), '', '', '', '', '', '', '', '']);
                    } else if (im.tipo === 'IVA' && im.deducoes) {
                        const d = im.deducoes;
                        linhas.push([escape('Tipo'), escape(`IVA ${im.taxa}%`), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('IVA Cobrado (bruto)'), escape(formatarMoeda(im.bruto)), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('IVA Dedutível Despesas'), escape(formatarMoeda(d.ivaDedutivelDespesas)), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('IVA Dedutível Stock'), escape(formatarMoeda(d.ivaDedutivelStock)), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('IVA Dedutível Compras'), escape(formatarMoeda(d.ivaDedutivelCompras)), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('IVA A Entregar'), escape(formatarMoeda(im.aEntregar)), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('IVA A Recuperar'), escape(formatarMoeda(im.aRecuperar)), '', '', '', '', '', '', '', '']);
                    } else if (im.tipo === 'IVA') {
                        linhas.push([escape('Tipo'), escape(`IVA ${im.taxa}%`), '', '', '', '', '', '', '', '']);
                        linhas.push([escape('IVA Cobrado'), escape(formatarMoeda(im.bruto)), '', '', '', '', '', '', '', '']);
                    } else {
                        linhas.push([escape('Tipo'), escape('Isento'), '', '', '', '', '', '', '', '']);
                    }
                }

            } else if (abaAtiva === 'produtos') {
                linhas.push(['Produto', 'Tipo', 'Qtd Vendida', 'Receita', 'Preço Médio', 'Custo', 'Margem Lucro', 'Margem %'].map(escape));
                (data.produtos || []).forEach(p => {
                    const row = [
                        p.nome, // 🔥 Escapado abaixo
                        p.tipo,
                        p.quantidadeVendida,
                        formatarMoeda(p.receitaTotal),
                        formatarMoeda(p.precoMedio),
                        formatarMoeda(p.custoTotal),
                        formatarMoeda(p.margemLucro),
                        `${p.margemPercent?.toFixed(1) ?? '0'}%`
                    ];
                    linhas.push(row.map(escape));
                });

            } else if (abaAtiva === 'vendedores') {
                linhas.push(['Vendedor', 'Email', 'Perfil', 'Nº Vendas', 'Faturamento', 'Ticket Médio'].map(escape));
                (data.vendedores || []).forEach(v => {
                    const row = [
                        v.nome,
                        v.email || '-',
                        v.perfil || '-',
                        v.numeroVendas,
                        formatarMoeda(v.totalFaturado),
                        formatarMoeda(v.ticketMedio)
                    ];
                    linhas.push(row.map(escape));
                });

            } else if (abaAtiva === 'inadimplencia') {
                linhas.push(['Nº Fatura', 'Cliente', 'Telefone', 'Vencimento', 'Dias Atraso', 'Total', 'Pago', 'Saldo Devedor'].map(escape));
                (data.faturas || []).forEach(f => {
                    const row = [
                        f.numero,
                        f.cliente?.nome || '-',
                        f.cliente?.telefone || '-',
                        formatarData(f.dataVencimento),
                        f.diasAtraso,
                        formatarMoeda(f.total),
                        formatarMoeda(f.valorPago),
                        formatarMoeda(f.saldoDevedor)
                    ];
                    linhas.push(row.map(escape));
                });
            }

            if (linhas.length <= 6) {
                alert('Sem dados para exportar.');
                return;
            }

            const csv = linhas.map(linha => linha.join(';')).join('\n');

            const abaLabels = { faturas: 'Faturas', produtos: 'Produtos', vendedores: 'Vendedores', inadimplencia: 'Inadimplencia' };
            const nomeArquivo = `Relatorio_Vendas_${abaLabels[abaAtiva] || 'Export'}_${new Date().toISOString().split('T')[0]}.csv`;

            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = nomeArquivo;
            link.click();
            URL.revokeObjectURL(link.href);

        } catch (err) {
            console.error('❌ Erro ao exportar CSV:', err);
            alert(err?.response?.data?.message || err.message || 'Erro ao exportar CSV');
        }
    };

    // =============================================
    // 🔥 BAIXAR PDF
    // =============================================
    const baixarPDF = async () => {
        if (exportandoPDF) return;
        setExportandoPDF(abaAtiva);
        try {
            const token = localStorage.getItem('token');
            if (!token) { alert('Sessão expirada. Faça login novamente.'); return; }

            const params = new URLSearchParams();
            params.append('aba', abaAtiva);
            params.append('pagina', '1');
            params.append('limite', '999999');

            if (filtrosAplicados.dataInicio) params.append('dataInicio', filtrosAplicados.dataInicio);
            if (filtrosAplicados.dataFim) params.append('dataFim', filtrosAplicados.dataFim);
            if (filtrosAplicados.clienteId) params.append('clienteId', filtrosAplicados.clienteId);
            if (filtrosAplicados.vendedorId) params.append('vendedorId', filtrosAplicados.vendedorId);
            if (filtrosAplicados.status) params.append('status', filtrosAplicados.status);

            const url = `/api/relatorios/vendas/exportar?${params.toString()}`;
            console.log('📄 [PDF] A pedir:', url);

            const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } });
            const contentType = (response.headers.get('content-type') || '').toLowerCase();
            const contentDisposition = (response.headers.get('content-disposition') || '').toLowerCase();

            if (!response.ok) {
                let erroMsg = `Erro ${response.status} ao gerar PDF`;
                try {
                    const errorData = await response.json();
                    erroMsg = errorData.message || errorData.erro || erroMsg;
                } catch (_) {
                    try {
                        const texto = await response.text();
                        if (texto) erroMsg = texto.slice(0, 300);
                    } catch (__) { /* ignora */ }
                }
                throw new Error(erroMsg);
            }

            const isPDF = contentType.includes('application/pdf') || contentDisposition.includes('.pdf');
            const isHTML = contentType.includes('text/html');

            if (isPDF) {
                const blob = await response.blob();
                let nomeFicheiro = null;
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (match && match[1]) nomeFicheiro = match[1].replace(/['"]/g, '');
                }
                if (!nomeFicheiro) {
                    const abaLabels = { faturas: 'Faturas', produtos: 'Produtos', vendedores: 'Vendedores', inadimplencia: 'Inadimplencia' };
                    const sufixo = abaLabels[abaAtiva] || 'Relatorio';
                    nomeFicheiro = `Relatorio_Vendas_${sufixo}_${new Date().toISOString().split('T')[0]}.pdf`;
                }
                const urlDownload = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = urlDownload;
                link.download = nomeFicheiro;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(urlDownload);
                return;
            }

            if (isHTML) {
                const html = await response.text();
                console.warn('⚠️ [PDF] Backend devolveu HTML em vez de PDF:', html.slice(0, 300));
                const printWindow = window.open('', '_blank', 'width=1200,height=800');
                if (!printWindow) { alert('O servidor devolveu HTML em vez de PDF e o pop-up foi bloqueado.'); return; }
                printWindow.document.open();
                printWindow.document.write(html);
                printWindow.document.close();
                printWindow.onload = () => { setTimeout(() => { printWindow.focus(); printWindow.print(); }, 400); };
                return;
            }
            throw new Error(`Tipo de resposta inesperado: ${contentType || 'desconhecido'}`);
        } catch (err) {
            console.error('❌ Erro ao baixar PDF:', err);
            alert(err.message || 'Falha ao gerar PDF');
        } finally {
            setExportandoPDF(null);
        }
    };

    // ==================== Info do imposto ====================
    const infoImposto = obterInfoImposto();

    // ==================== RENDER ====================
    return (
        <div className="relatorio-vendas-container">
            {/* HEADER */}
            <div className="rv-header">
                <div className="rv-titulo-area">
                    <h1>
                        <Icon name="description" size={26} /> Relatório de Vendas
                        {regime.tipo === 'ISPC' && (
                            <span className="rv-badge-regime ispc" title="Regime ISPC — Isento de IVA, paga 15% trimestral">ISPC 15%</span>
                        )}
                        {regime.tipo === 'Normal' && (
                            <span className="rv-badge-regime normal" title={`Regime Normal — IVA ${regime.taxaIVA}%`}>IVA {regime.taxaIVA}%</span>
                        )}
                        {regime.tipo === 'Isento' && (
                            <span className="rv-badge-regime isento" title="Regime Isento">Isento</span>
                        )}
                    </h1>
                    <p className="rv-subtitulo">Análise detalhada • Exportação • Auditoria</p>
                </div>
                <div className="rv-acoes">
                    <button className="btn-secundario" onClick={limparFiltros}>
                        <Icon name="refresh" size={16} />
                    </button>
                    {/* <button className="btn-secundario" onClick={baixarPDF} disabled={exportandoPDF !== null} title="Baixar Relatório em PDF">
                        {exportandoPDF ? (
                            <><div className="spinner-small" />A gerar PDF...</>
                        ) : (
                            <><Icon name="picture_as_pdf" size={16} /> Baixar PDF</>
                        )}
                    </button>*/}
                    <button className="btn-primario" onClick={exportarCSV}>
                        <Icon name="file_download" size={16} /> Exportar CSV
                    </button>
                </div>
            </div>

            {/* FILTROS */}
            <div className="rv-filtros">
                <div className="rv-filtro-grupo">
                    <label><Icon name="calendar_today" size={12} /> Data Início</label>
                    <input type="date" name="dataInicio" value={filtrosRascunho.dataInicio} onChange={handleFiltroChange} />
                </div>
                <div className="rv-filtro-grupo">
                    <label><Icon name="calendar_today" size={12} /> Data Fim</label>
                    <input type="date" name="dataFim" value={filtrosRascunho.dataFim} onChange={handleFiltroChange} />
                </div>
                {abaAtiva === 'faturas' && (
                    <>
                        <div className="rv-filtro-grupo">
                            <label><Icon name="filter_list" size={12} /> Status</label>
                            <select name="status" value={filtrosRascunho.status} onChange={handleFiltroChange}>
                                <option value="Emitida,Aberta,Pago,Vencida">Todas</option>
                                <option value="Pago">Pagas</option>
                                <option value="Emitida,Aberta">Em Aberto</option>
                                <option value="Vencida">Vencidas</option>
                            </select>
                        </div>
                        <div className="rv-filtro-grupo">
                            <label><Icon name="person" size={12} /> Vendedor</label>
                            <select name="vendedorId" value={filtrosRascunho.vendedorId} onChange={handleFiltroChange}>
                                <option value="">Todos</option>
                                {vendedoresDisponiveis.map(v => (
                                    <option key={v.id} value={v.id}>{v.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="rv-filtro-grupo rv-filtro-cliente">
                            <label><Icon name="search" size={12} /> Cliente</label>
                            <ClienteSearch clientes={clientes} clienteSelecionado={filtrosRascunho.clienteId} onSelecionar={handleClienteSelecionar} onLimpar={handleClienteLimpar} />
                        </div>
                    </>
                )}
                <div className="rv-filtro-grupo rv-filtro-aplicar">
                    <label>&nbsp;</label>
                    <button className="btn-primario" onClick={aplicarFiltros}>
                        <Icon name="search" size={14} /> Aplicar
                    </button>
                </div>
            </div>

            {/* ABAS */}
            <div className="rv-abas">
                <button className={`rv-aba ${abaAtiva === 'faturas' ? 'ativa' : ''}`} onClick={() => setAbaAtiva('faturas')}><FiFileText /> Faturas</button>
                <button className={`rv-aba ${abaAtiva === 'produtos' ? 'ativa' : ''}`} onClick={() => setAbaAtiva('produtos')}><FiBox /> Produtos & Margem</button>
                <button className={`rv-aba ${abaAtiva === 'vendedores' ? 'ativa' : ''}`} onClick={() => setAbaAtiva('vendedores')}><FiUsers /> Vendedores</button>
                <button className={`rv-aba ${abaAtiva === 'inadimplencia' ? 'ativa' : ''}`} onClick={() => setAbaAtiva('inadimplencia')}><FiAlertTriangle /> Inadimplência</button>
            </div>

            {/* ERRO */}
            {erro && (
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 'var(--radius-md)', padding: '12px 18px', marginBottom: '16px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem' }}>
                    <Icon name="error" size={20} /> <span>{erro}</span>
                    <button onClick={() => setErro(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1.2rem' }}>×</button>
                </div>
            )}

            {/* CONTEÚDO */}
            <div className="rv-conteudo">
                {carregando ? (
                    <div className="rv-loading"><div className="spinner-large"></div><p>A carregar dados...</p></div>
                ) : (
                    <>
                        {/* FATURAS */}
                        {abaAtiva === 'faturas' && (
                            <>
                                <div className="rv-cards-totais">
                                    {regime.tipo === 'ISPC' ? (
                                        <>
                                            <div className="rv-card-total"><span>Total Faturado</span><strong>{formatarMoeda(faturas.totais.totalFaturado)}</strong></div>
                                            <div className="rv-card-total"><span>Volume de Negócios</span><strong>{formatarMoeda(faturas.totais.totalFaturado)}</strong></div>
                                            <div className="rv-card-total verde"><span>Total Pago</span><strong>{formatarMoeda(faturas.totais.totalPago)}</strong></div>
                                            <div className="rv-card-total laranja"><span>Saldo em Aberto</span><strong>{formatarMoeda(faturas.totais.totalSaldo)}</strong></div>
                                        </>
                                    ) : regime.tipo === 'Isento' ? (
                                        <>
                                            <div className="rv-card-total"><span>Total Faturado</span><strong>{formatarMoeda(faturas.totais.totalFaturado)}</strong></div>
                                            <div className="rv-card-total"><span>Imposto</span><strong>Isento</strong></div>
                                            <div className="rv-card-total verde"><span>Total Pago</span><strong>{formatarMoeda(faturas.totais.totalPago)}</strong></div>
                                            <div className="rv-card-total laranja"><span>Saldo em Aberto</span><strong>{formatarMoeda(faturas.totais.totalSaldo)}</strong></div>
                                        </>
                                    ) : (
                                        <>
                                            {(() => {
                                                const temDeducoes = infoImposto.deducoes && infoImposto.deducoes.ivaDedutivel > 0;
                                                const ivaAEntregar = infoImposto.aEntregar || 0;
                                                const ivaARecuperar = infoImposto.aRecuperar || 0;
                                                let ivaLabel, ivaTexto;
                                                if (temDeducoes) {
                                                    ivaLabel = ivaAEntregar > 0 ? `IVA ${regime.taxaIVA}% a Entregar` : `IVA ${regime.taxaIVA}% a Recuperar`;
                                                    ivaTexto = ivaAEntregar > 0 ? formatarMoeda(ivaAEntregar) : formatarMoeda(ivaARecuperar);
                                                } else {
                                                    ivaLabel = `IVA ${regime.taxaIVA}%`;
                                                    ivaTexto = formatarMoeda(infoImposto.bruto);
                                                }
                                                return (
                                                    <>
                                                        <div className="rv-card-total"><span>Total Faturado</span><strong>{formatarMoeda(faturas.totais.totalFaturado)}</strong></div>
                                                        <div className="rv-card-total"><span>{ivaLabel}</span><strong>{ivaTexto}</strong></div>
                                                        <div className="rv-card-total verde"><span>Total Pago</span><strong>{formatarMoeda(faturas.totais.totalPago)}</strong></div>
                                                        <div className="rv-card-total laranja"><span>Saldo em Aberto</span><strong>{formatarMoeda(faturas.totais.totalSaldo)}</strong></div>
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>

                                <div className="rv-tabela-wrapper">
                                    <table className="rv-tabela">
                                        <thead>
                                            <tr>
                                                <th>Nº Fatura</th>
                                                <th>Data</th>
                                                <th>Cliente</th>
                                                <th>NUIT</th>
                                                <th>Vendedor</th>
                                                <th>Status</th>
                                                <th className="text-right">Subtotal</th>
                                                {mostrarColunaImposto && <th className="text-right">IVA {regime.taxaIVA}%</th>}
                                                <th className="text-right">Total</th>
                                                <th className="text-right">Saldo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {faturas.dados.length === 0 ? (
                                                <tr><td colSpan={mostrarColunaImposto ? 10 : 9} className="rv-sem-dados">Sem faturas para os filtros selecionados.</td></tr>
                                            ) : (
                                                faturas.dados.map(f => (
                                                    <tr key={f.id}>
                                                        <td className="mono">{f.numero}</td>
                                                        <td>{formatarData(f.dataEmissao)}</td>
                                                        <td>{f.cliente?.nome || '-'}</td>
                                                        <td className="mono">{f.cliente?.nuit || '-'}</td>
                                                        <td>{f.vendedor?.nome || '-'}</td>
                                                        <td><span className={`badge-status ${(f.status || '').toLowerCase()}`}>{f.status}</span></td>
                                                        <td className="text-right">{formatarMoeda(f.subtotal)}</td>
                                                        {mostrarColunaImposto && (
                                                            <td className="text-right">
                                                                {f.iva > 0 ? <>{formatarMoeda(f.iva)}{f.ivaPercent > 0 && <small style={{ color: '#6c757d', fontSize: '0.7em' }}> ({f.ivaPercent}%)</small>}</> : '—'}
                                                            </td>
                                                        )}
                                                        <td className="text-right destaque">{formatarMoeda(f.total)}</td>
                                                        <td className={`text-right ${f.saldo > 0 ? 'vermelho' : 'verde'}`}>{formatarMoeda(f.saldo)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <Paginacao paginaAtual={paginas.faturas} totalPaginas={faturas.paginacao?.totalPaginas || 1} total={faturas.paginacao?.total || 0} limite={LIMITE_POR_PAGINA} onMudarPagina={(p) => mudarPagina('faturas', p)} />
                                <BlocoISPC imposto={infoImposto} formatarMoeda={formatarMoeda} taxa={regime.taxaISPC} />
                                <BlocoIVA imposto={infoImposto} formatarMoeda={formatarMoeda} taxa={regime.taxaIVA} />
                            </>
                        )}

                        {/* PRODUTOS */}
                        {abaAtiva === 'produtos' && (
                            <>
                                <div className="rv-tabela-wrapper">
                                    <table className="rv-tabela">
                                        <thead>
                                            <tr><th>Produto/Serviço</th><th>Tipo</th><th className="text-right">Qtd</th><th className="text-right">Preço Médio</th><th className="text-right">Receita</th><th className="text-right">Custo</th><th className="text-right">Margem</th><th className="text-right">Margem %</th></tr>
                                        </thead>
                                        <tbody>
                                            {produtos.dados.length === 0 ? (
                                                <tr><td colSpan="8" className="rv-sem-dados">Sem produtos vendidos no período.</td></tr>
                                            ) : (
                                                produtos.dados.map((p, i) => (
                                                    <tr key={i}>
                                                        <td><strong>{p.nome}</strong></td>
                                                        <td><span className="badge-tipo">{p.tipo}</span></td>
                                                        <td className="text-right">{p.quantidadeVendida}</td>
                                                        <td className="text-right">{formatarMoeda(p.precoMedio)}</td>
                                                        <td className="text-right destaque">{formatarMoeda(p.receitaTotal)}</td>
                                                        <td className="text-right">{formatarMoeda(p.custoTotal)}</td>
                                                        <td className={`text-right ${p.margemLucro > 0 ? 'verde' : 'vermelho'}`}>{formatarMoeda(p.margemLucro)}</td>
                                                        <td className={`text-right ${p.margemPercent > 20 ? 'verde' : p.margemPercent > 0 ? 'laranja' : 'vermelho'}`}>{p.margemPercent?.toFixed(1) ?? '0'}%</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <Paginacao paginaAtual={paginas.produtos} totalPaginas={produtos.paginacao?.totalPaginas || 1} total={produtos.paginacao?.total || 0} limite={LIMITE_POR_PAGINA} onMudarPagina={(p) => mudarPagina('produtos', p)} />
                            </>
                        )}

                        {/* VENDEDORES */}
                        {abaAtiva === 'vendedores' && (
                            <>
                                <div className="rv-tabela-wrapper">
                                    <table className="rv-tabela">
                                        <thead>
                                            <tr><th>#</th><th>Vendedor</th><th>Email</th><th>Perfil</th><th className="text-right">Nº Vendas</th><th className="text-right">Ticket Médio</th><th className="text-right">Faturamento</th></tr>
                                        </thead>
                                        <tbody>
                                            {vendedores.dados.length === 0 ? (
                                                <tr><td colSpan="7" className="rv-sem-dados">Sem vendas atribuídas a vendedores.</td></tr>
                                            ) : (
                                                vendedores.dados.map((v, i) => {
                                                    const posicaoReal = (paginas.vendedores - 1) * LIMITE_POR_PAGINA + i + 1;
                                                    return (
                                                        <tr key={v.vendedorId || i}>
                                                            <td className="posicao">{posicaoReal}º</td>
                                                            <td><strong>{v.nome}</strong></td>
                                                            <td className="mono">{v.email || '-'}</td>
                                                            <td>{v.perfil ? <span className="badge-tipo">{v.perfil}</span> : '-'}</td>
                                                            <td className="text-right">{v.numeroVendas}</td>
                                                            <td className="text-right">{formatarMoeda(v.ticketMedio)}</td>
                                                            <td className="text-right destaque">{formatarMoeda(v.totalFaturado)}</td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <Paginacao paginaAtual={paginas.vendedores} totalPaginas={vendedores.paginacao?.totalPaginas || 1} total={vendedores.paginacao?.total || 0} limite={LIMITE_POR_PAGINA} onMudarPagina={(p) => mudarPagina('vendedores', p)} />
                            </>
                        )}

                        {/* INADIMPLÊNCIA */}
                        {abaAtiva === 'inadimplencia' && (
                            <>
                                <div className="rv-cards-totais">
                                    <div className="rv-card-total vermelho"><span>Total Vencido</span><strong>{formatarMoeda(inadimplencia.totais.totalVencido)}</strong></div>
                                    <div className="rv-card-total laranja"><span>Total a Receber</span><strong>{formatarMoeda(inadimplencia.totais.totalAReceber)}</strong></div>
                                    <div className="rv-card-total"><span>Nº Faturas em Aberto</span><strong>{inadimplencia.totais.numeroFaturas}</strong></div>
                                </div>

                                <div className="rv-tabela-wrapper">
                                    <table className="rv-tabela">
                                        <thead>
                                            <tr><th>Nº Fatura</th><th>Cliente</th><th>Telefone</th><th>Vencimento</th><th className="text-right">Dias Atraso</th><th className="text-right">Total</th><th className="text-right">Pago</th><th className="text-right">Saldo Devedor</th></tr>
                                        </thead>
                                        <tbody>
                                            {inadimplencia.dados.length === 0 ? (
                                                <tr><td colSpan="8" className="rv-sem-dados">🎉 Nenhuma fatura em atraso!</td></tr>
                                            ) : (
                                                inadimplencia.dados.map(f => (
                                                    <tr key={f.id}>
                                                        <td className="mono">{f.numero}</td>
                                                        <td>{f.cliente?.nome || '-'}</td>
                                                        <td className="mono">{f.cliente?.telefone || '-'}</td>
                                                        <td>{formatarData(f.dataVencimento)}</td>
                                                        <td className={`text-right ${f.diasAtraso > 30 ? 'vermelho' : 'laranja'}`}>{f.diasAtraso > 0 ? `${f.diasAtraso} dias` : 'Vence hoje'}</td>
                                                        <td className="text-right">{formatarMoeda(f.total)}</td>
                                                        <td className="text-right verde">{formatarMoeda(f.valorPago)}</td>
                                                        <td className="text-right vermelho destaque">{formatarMoeda(f.saldoDevedor)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <Paginacao paginaAtual={paginas.inadimplencia} totalPaginas={inadimplencia.paginacao?.totalPaginas || 1} total={inadimplencia.paginacao?.total || 0} limite={LIMITE_POR_PAGINA} onMudarPagina={(p) => mudarPagina('inadimplencia', p)} />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}